import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "./AdminPanel/Sidebar";
import TopBar from "./AdminPanel/TopBar";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

export default function AdminRegister() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { showError, showSuccess } = useAlert();
  const { user } = useAuth();
  const navigate = useNavigate();

  const token = localStorage.getItem("accessToken");
  const permissions = user?.permissions || [];
  const canViewVentas = user?.role === "super_admin" || permissions.includes("ventas.view");
  const canCreateVentas = user?.role === "super_admin" || permissions.includes("ventas.create");
  const canEditVentas = user?.role === "super_admin" || permissions.includes("ventas.edit");

  const [loading, setLoading] = useState(false);
  const [cajaActual, setCajaActual] = useState(null);
  const [movimientos, setMovimientos] = useState([]);

  const [empresaId, setEmpresaId] = useState("1");
  const [montoInicial, setMontoInicial] = useState("");
  const [observacionApertura, setObservacionApertura] = useState("");

  const [efectivoContado, setEfectivoContado] = useState("");
  const [transferenciaContada, setTransferenciaContada] = useState("");
  const [observacionCierre, setObservacionCierre] = useState("");

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const cardSx = {
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
  };

  const labelSx = {
    fontSize: 13,
    fontWeight: 500,
    color: dashboardTheme.muted_text_color,
    lineHeight: 1.3,
  };

  const valueSx = {
    fontSize: 22,
    fontWeight: 700,
    color: dashboardTheme.text_color,
    lineHeight: 1.2,
  };

  const estadoCaja = cajaActual?.estado || "SIN_CAJA";

  const resumenCaja = useMemo(() => {
    return [
      {
        label: "Monto inicial",
        value: `$${formatMoney(cajaActual?.monto_inicial)}`,
        icon: <AccountBalanceWalletRoundedIcon sx={{ color: dashboardTheme.primary_color }} />,
      },
      {
        label: "Efectivo sistema",
        value: `$${formatMoney(cajaActual?.total_efectivo_sistema)}`,
        icon: <PointOfSaleRoundedIcon sx={{ color: dashboardTheme.primary_color }} />,
      },
      {
        label: "Transferencias sistema",
        value: `$${formatMoney(cajaActual?.total_transferencia_sistema)}`,
        icon: <CreditCardRoundedIcon sx={{ color: dashboardTheme.primary_color }} />,
      },
      {
        label: "Efectivo esperado",
        value: `$${formatMoney(cajaActual?.efectivo_esperado_cierre)}`,
        icon: <ReceiptLongRoundedIcon sx={{ color: dashboardTheme.primary_color }} />,
      },
    ];
  }, [cajaActual, dashboardTheme.primary_color]);

  const loadCajaActual = async () => {
    if (!canViewVentas) return;

    try {
      setLoading(true);

      const response = await fetch("/api/ventas/caja/actual/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo consultar la caja");
        return;
      }

      const caja = data.caja || null;
      setCajaActual(caja);

      if (caja?.id) {
        loadDetalleCaja(caja.id);
      } else {
        setMovimientos([]);
      }
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const loadDetalleCaja = async (cajaId) => {
    try {
      const response = await fetch(`/api/ventas/caja/${cajaId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo cargar el detalle de caja");
        return;
      }

      setCajaActual(data.caja || null);
      setMovimientos(data.movimientos || []);
    } catch {
      showError("Error de conexión");
    }
  };

  const abrirCaja = async () => {
    if (!canCreateVentas) {
      showError("No tienes permisos para abrir caja");
      return;
    }

    if (!empresaId || !montoInicial) {
      showError("Ingresa la empresa y el monto inicial");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/ventas/caja/abrir/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          empresa: empresaId,
          monto_inicial: montoInicial,
          observacion_apertura: observacionApertura,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo abrir la caja");
        return;
      }

      showSuccess(data.mensaje || "Caja abierta correctamente");
      setCajaActual(data.caja || null);
      setMontoInicial("");
      setObservacionApertura("");

      if (data.caja?.id) {
        loadDetalleCaja(data.caja.id);
      }
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const cerrarCaja = async () => {
    if (!canEditVentas) {
      showError("No tienes permisos para cerrar caja");
      return;
    }

    if (!cajaActual?.id) {
      showError("No tienes caja abierta");
      return;
    }

    if (!efectivoContado) {
      showError("Ingresa el efectivo contado");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`/api/ventas/caja/${cajaActual.id}/cerrar/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          efectivo_contado_cierre: efectivoContado,
          transferencia_contada_cierre: transferenciaContada || "0.00",
          observacion_cierre: observacionCierre,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo cerrar la caja");
        return;
      }

      showSuccess(data.mensaje || "Caja cerrada correctamente");
      setCajaActual(data.caja || null);
      setMovimientos([]);
      setEfectivoContado("");
      setTransferenciaContada("");
      setObservacionCierre("");
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewVentas) {
      showError("No tienes permisos para acceder a caja");
      navigate("/admin-panel", { replace: true });
      return;
    }

    loadCajaActual();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 24, sm: 26, md: 30 },
                    fontWeight: 600,
                    color: dashboardTheme.primary_color,
                    lineHeight: 1.2,
                  }}
                >
                  Caja
                </Typography>
              </Box>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={dashboardTheme.tableStyles.toolbar}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                      }}
                    >
                      Estado: {estadoCaja === "ABIERTA" ? "Caja abierta" : "Sin caja abierta"}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 400,
                        color: dashboardTheme.muted_text_color,
                        mt: 0.3,
                      }}
                    >
                      {cajaActual?.fecha_apertura
                        ? `Apertura: ${formatDate(cajaActual.fecha_apertura)}`
                        : "Abre caja para empezar a facturar"}
                    </Typography>
                  </Box>

                  {estadoCaja === "ABIERTA" && (
                    <Box
                      sx={{
                        px: 1.4,
                        py: 0.8,
                        borderRadius: "999px",
                        bgcolor: `${dashboardTheme.primary_color}18`,
                        color: dashboardTheme.primary_color,
                        fontSize: 13,
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      Caja #{cajaActual?.id}
                    </Box>
                  )}
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                {!cajaActual && (
                  <Box sx={cardSx}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <LockOpenRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: dashboardTheme.text_color,
                        }}
                      >
                        Abrir caja
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                      <TextField
                        size="small"
                        label="ID Empresa"
                        value={empresaId}
                        onChange={(e) => setEmpresaId(e.target.value)}
                        sx={{ minWidth: { xs: "100%", md: 130 }, ...dashboardTheme.inputStyles }}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Monto inicial"
                        value={montoInicial}
                        onChange={(e) => setMontoInicial(e.target.value)}
                        sx={dashboardTheme.inputStyles}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Observación"
                        value={observacionApertura}
                        onChange={(e) => setObservacionApertura(e.target.value)}
                        sx={dashboardTheme.inputStyles}
                      />

                      <Button
                        variant="contained"
                        startIcon={<LockOpenRoundedIcon />}
                        onClick={abrirCaja}
                        disabled={loading}
                        sx={dashboardTheme.buttonStyles.contained}
                      >
                        Abrir caja
                      </Button>
                    </Stack>
                  </Box>
                )}

                {cajaActual && (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                          md: "repeat(4, 1fr)",
                        },
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      {resumenCaja.map((item) => (
                        <Box key={item.label} sx={cardSx}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                            {item.icon}
                            <Typography sx={labelSx}>{item.label}</Typography>
                          </Stack>
                          <Typography sx={valueSx}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={cardSx}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <LockRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
                        <Typography
                          sx={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: dashboardTheme.text_color,
                          }}
                        >
                          Cerrar caja
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Efectivo contado"
                          value={efectivoContado}
                          onChange={(e) => setEfectivoContado(e.target.value)}
                          sx={dashboardTheme.inputStyles}
                        />

                        <TextField
                          fullWidth
                          size="small"
                          label="Transferencia contada"
                          value={transferenciaContada}
                          onChange={(e) => setTransferenciaContada(e.target.value)}
                          sx={dashboardTheme.inputStyles}
                        />

                        <TextField
                          fullWidth
                          size="small"
                          label="Observación cierre"
                          value={observacionCierre}
                          onChange={(e) => setObservacionCierre(e.target.value)}
                          sx={dashboardTheme.inputStyles}
                        />

                        <Button
                          variant="outlined"
                          startIcon={<LockRoundedIcon />}
                          onClick={cerrarCaja}
                          disabled={loading}
                          sx={outlinedButtonSx}
                        >
                          Cerrar caja
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Fecha
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Origen
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Tipo
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Forma pago
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Concepto
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Monto
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {movimientos.map((movimiento) => (
                        <TableRow key={movimiento.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDate(movimiento.creado_en)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {movimiento.origen === "VENTA"
                              ? "Venta"
                              : movimiento.origen === "COMPRA"
                                ? "Compra"
                                : "Sistema"}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {movimiento.tipo === "INGRESO" ? "Ingreso" : "Egreso"}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {movimiento.forma_pago === "TRANSFERENCIA" ? "Transferencia" : "Efectivo"}
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: dashboardTheme.text_color,
                                  lineHeight: 1.25,
                                }}
                              >
                                {movimiento.concepto || "-"}
                              </Typography>

                              {(movimiento.referencia || movimiento.observacion) && (
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    fontWeight: 400,
                                    color: dashboardTheme.muted_text_color,
                                    mt: 0.3,
                                  }}
                                >
                                  {movimiento.referencia || movimiento.observacion}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            ${formatMoney(movimiento.monto)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading && movimientos.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No hay movimientos registrados
                          </TableCell>
                        </TableRow>
                      )}

                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Cargando caja...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}